import { supabase } from '@/core/database/supabase';
import type { 
  MaintenanceSchedule, 
  ScheduleStats, 
  ScheduleFilters 
} from '../types/maintenance-schedules.types';

export const MaintenanceSchedulesService = {
  /**
   * Lấy danh sách lịch bảo dưỡng
   */
  async getSchedules(companyId: string, filters: ScheduleFilters = {}): Promise<{ data: MaintenanceSchedule[], total: number }> {
    let query = supabase
      .from('maintenance_schedules')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, zone_name:name),
        plan:maintenance_plans(id, name),
        team:teams(id, name),
        assignee:user_profiles!assigned_user_id(id, full_name, avatar_url:avatar_path),
        work_order:work_orders(id, work_order_code, status_code)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.teamId) query = query.eq('assigned_team_id', filters.teamId);
    if (filters.dateRange) {
      query = query.gte('scheduled_date', filters.dateRange.start).lte('scheduled_date', filters.dateRange.end);
    }
    if (filters.search) {
      query = query.or(`code.ilike.%${filters.search}%`);
    }

    query = query.order('scheduled_date', { ascending: true });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceSchedule[],
      total: count || 0
    };
  },

  /**
   * Chi tiết lịch trình
   */
  async getScheduleDetail(id: string): Promise<MaintenanceSchedule> {
    const { data, error } = await supabase
      .from('maintenance_schedules')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, zone_name:name),
        plan:maintenance_plans(id, name),
        team:teams(id, name),
        assignee:user_profiles!assigned_user_id(id, full_name, avatar_url:avatar_path),
        work_order:work_orders(id, work_order_code, status_code)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as MaintenanceSchedule;
  },

  /**
   * Thống kê lịch trình
   */
  async getScheduleStats(companyId: string): Promise<ScheduleStats> {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('maintenance_schedules')
      .select('status_code, scheduled_date')
      .eq('company_id', companyId);

    if (error) throw error;

    const todayItems = data.filter(s => s.scheduled_date === today);
    const completed = data.filter(s => s.status_code === 'completed').length;

    return {
      todaySchedules: todayItems.length,
      assignedSchedules: data.filter(s => s.status_code === 'assigned').length,
      inProgressSchedules: data.filter(s => s.status_code === 'in_progress').length,
      delayedSchedules: data.filter(s => s.status_code === 'delayed').length,
      completionRate: data.length > 0 ? (completed / data.length) * 100 : 0,
    };
  },

  /**
   * CRUD Operations
   */
  async createSchedule(data: Partial<MaintenanceSchedule>): Promise<MaintenanceSchedule> {
    const { data: result, error } = await supabase
      .from('maintenance_schedules')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceSchedule;
  },

  async updateSchedule(id: string, updates: Partial<MaintenanceSchedule>): Promise<MaintenanceSchedule> {
    const { data, error } = await supabase
      .from('maintenance_schedules')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as MaintenanceSchedule;
  },

  /**
   * Phát sinh Work Order từ lịch
   */
  async generateWorkOrder(scheduleId: string): Promise<any> {
    // Logic gọi RPC hoặc Edge Function để tạo WO từ Schedule
    const { data, error } = await supabase.rpc('generate_work_order_from_schedule', { schedule_id: scheduleId });
    if (error) throw error;
    return data;
  }
};

